import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Pagination } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import { NeoFeedSelectedSourceChip, NeoFeedSuggestionCard } from './neoFeedSuggestionCards';

const PRIMARY = '#f25d27';
const SUGGESTIONS_PAGE_SIZE = 5;

/**
 * Painel colapsável de "Sugestões NeoFeed" usado na coluna lateral das
 * telas de matéria existente e de escrita via inteligência editorial. A
 * busca é disparada pela página (automaticamente a cada versão gerada,
 * enquanto o toggle "Usar sugestões NeoFeed" estiver ativo); este
 * componente só exibe o resultado e a seleção.
 *
 * @typedef {import('../../../types/articles').NeoFeedContextSuggestion} NeoFeedContextSuggestion
 * @typedef {import('../../../types/articles').NeoFeedContextSelectionEntry} NeoFeedContextSelectionEntry
 *
 * @param {{
 *   expanded: boolean;
 *   onToggleExpanded: () => void;
 *   suggestions: NeoFeedContextSuggestion[];
 *   loading: boolean;
 *   error: string | null;
 *   selectedSources: Record<string, NeoFeedContextSelectionEntry>;
 *   onToggleSuggestion: (suggestion: NeoFeedContextSuggestion) => void;
 *   onToggleChunk: (externalArticleId: string, chunkId: string) => void;
 *   onChangeUsageMode: (externalArticleId: string, mode: string) => void;
 *   onRemoveSource: (externalArticleId: string) => void;
 *   disabled?: boolean;
 *   emptyMessage?: string;
 * }} props
 */
export default function NeoFeedSuggestionsPanel({
    expanded,
    onToggleExpanded,
    suggestions,
    loading,
    error,
    selectedSources,
    onToggleSuggestion,
    onToggleChunk,
    onChangeUsageMode,
    onRemoveSource,
    disabled = false,
    emptyMessage = 'Nenhuma sugestão encontrada para esta versão.',
    embedded = false,
}) {
    const selectedEntries = Object.entries(selectedSources || {});
    const selectedCount = selectedEntries.length;
    const isExpanded = embedded ? true : expanded;
    const [suggestionsPage, setSuggestionsPage] = useState(0);
    const totalSuggestions = Array.isArray(suggestions) ? suggestions.length : 0;
    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(totalSuggestions / SUGGESTIONS_PAGE_SIZE)),
        [totalSuggestions]
    );
    const visibleSuggestions = useMemo(() => {
        const start = suggestionsPage * SUGGESTIONS_PAGE_SIZE;
        return (suggestions || []).slice(start, start + SUGGESTIONS_PAGE_SIZE);
    }, [suggestions, suggestionsPage]);
    const rangeStart = totalSuggestions === 0 ? 0 : suggestionsPage * SUGGESTIONS_PAGE_SIZE + 1;
    const rangeEnd = Math.min((suggestionsPage + 1) * SUGGESTIONS_PAGE_SIZE, totalSuggestions);

    useEffect(() => {
        setSuggestionsPage(0);
    }, [suggestions]);

    return (
        <Paper sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: embedded ? '8px' : '12px',
            boxShadow: embedded ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
            border: embedded ? '1px solid #f0f0f0' : 'none',
            overflow: 'hidden',
            flex: isExpanded ? 1 : '0 0 auto',
            minHeight: 0,
        }}>
            {!embedded && (
                <Box
                    onClick={onToggleExpanded}
                    sx={{
                        p: '14px 18px',
                        borderBottom: isExpanded ? '1px solid #f0f0f0' : 'none',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: `${PRIMARY}05` },
                    }}
                >
                    <TravelExploreIcon sx={{ fontSize: 16, color: PRIMARY, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#333' }}>
                            Sugestões NeoFeed
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#999', mt: 0.2 }}>
                            {loading
                                ? 'Buscando na base NeoFeed…'
                                : selectedCount > 0
                                    ? `${selectedCount} fonte${selectedCount > 1 ? 's' : ''} selecionada${selectedCount > 1 ? 's' : ''}`
                                    : 'Artigos que podem auxiliar na escrita'}
                        </Typography>
                    </Box>
                    {loading && <CircularProgress size={14} sx={{ color: PRIMARY, flexShrink: 0 }} />}
                    {isExpanded ? <ExpandLessIcon sx={{ color: '#999' }} /> : <ExpandMoreIcon sx={{ color: '#999' }} />}
                </Box>
            )}

            {isExpanded && (
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    p: embedded ? '10px 12px' : '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}>
                    {selectedEntries.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {selectedEntries.map(([externalArticleId, entry]) => (
                                <NeoFeedSelectedSourceChip
                                    key={externalArticleId}
                                    entry={entry}
                                    disabled={disabled}
                                    simplified
                                    onRemove={() => onRemoveSource(externalArticleId)}
                                />
                            ))}
                        </Box>
                    )}

                    {error && (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.8,
                            p: '8px 10px', borderRadius: '8px',
                            backgroundColor: '#fdecea', border: '1px solid #f5c6c2',
                        }}>
                            <ErrorOutlineIcon sx={{ fontSize: 16, color: '#d32f2f', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.72rem', color: '#c62828', flex: 1 }}>
                                {error}
                            </Typography>
                        </Box>
                    )}

                    {!loading && !error && suggestions.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <NewspaperOutlinedIcon sx={{ fontSize: 26, color: '#e0e0e0', mb: 0.6 }} />
                            <Typography sx={{ fontSize: '0.78rem', color: '#bbb' }}>
                                {emptyMessage}
                            </Typography>
                        </Box>
                    )}

                    {!loading && suggestions.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                            {visibleSuggestions.map((suggestion) => (
                                <NeoFeedSuggestionCard
                                    key={suggestion.external_article_id}
                                    suggestion={suggestion}
                                    entry={selectedSources?.[suggestion.external_article_id]}
                                    disabled={disabled}
                                    simplified
                                    onToggleSelected={onToggleSuggestion}
                                    onToggleChunk={onToggleChunk}
                                    onChangeUsageMode={onChangeUsageMode}
                                />
                            ))}
                        </Box>
                    )}

                    {!loading && !error && totalSuggestions > 0 && totalPages > 1 && (
                        <Box sx={{
                            pt: 0.2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                        }}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>
                                {rangeStart}-{rangeEnd} de {totalSuggestions}
                            </Typography>
                            <Pagination
                                count={totalPages}
                                page={suggestionsPage + 1}
                                onChange={(_, page) => setSuggestionsPage(page - 1)}
                                size="small"
                                siblingCount={0}
                                boundaryCount={1}
                                sx={{
                                    '& .MuiPaginationItem-root.Mui-selected': {
                                        backgroundColor: PRIMARY,
                                        color: '#fff',
                                        '&:hover': { backgroundColor: '#d44e1f' },
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
}
